import { action, computed, makeObservable, observable } from 'mobx';
import { AgtAkumaAktionenModel } from './agt.akumaaktionen.store';
import { AggregateFunction, AgtAnalyticsStore, SelectColumn } from './agt.analytics.store';
import { SessionStore } from './session.store';

export class AkumaCampaignModel {
	constructor(data: any) {
		makeObservable(this);
		this.id = data.campaign_number;
		this.campaignName = data.campaign_name;
		this.focusCampaign = data.focus_campaign === 1;
		this.topCampaign = data.top_campaign === 1;
		this.lobUebergreifend = data.lob_uebergreifend === 1;
		this.lobSach = data.lob_sach === 1;
		this.lobLeben = data.lob_leben === 1;
		this.lobKranken = data.lob_kranken === 1;
		this.lobAgi = data.lob_agi === 1;
		this.lobWuestenrot = data.lob_wuestenrot === 1;
		this.lobSonstiges = data.lob_sonstiges === 1;
		this.baFirmen = data.ba_firmen === 1;
		this.baPrivat = data.ba_privat === 1;
		this.baSonstige = data.ba_sonstige === 1;
		this.campaignStartDate = new Date(data.campaign_start_date);
		this.campaignEndDate = new Date(data.campaign_end_date);
	}
	id: number;
	campaignName: string;
	focusCampaign: boolean;
	topCampaign: boolean;
	lobUebergreifend: boolean;
	lobSach: boolean;
	lobLeben: boolean;
	lobKranken: boolean;
	lobAgi: boolean;
	lobWuestenrot: boolean;
	lobSonstiges: boolean;
	baFirmen: boolean;
	baPrivat: boolean;
	baSonstige: boolean;
	campaignStartDate: Date;
	campaignEndDate: Date;
	@observable
	item?: AgtAkumaAktionenModel;

	@observable
	hasItem: boolean = false;

	@action
	setItem(item?: AgtAkumaAktionenModel) {
		if (item) {
			this.item = item;
			this.hasItem = true;
		} else {
			this.item = new AgtAkumaAktionenModel({ vtnr_id: 0, kw: 0, campaign_number: 0 });
			this.hasItem = false;
		}
	}

	@computed
	get sparte() {
		const lobFields = ['lobUebergreifend', 'lobSach', 'lobLeben', 'lobKranken', 'lobAgi', 'lobWuestenrot', 'lobSonstiges'] as const;
		const trueFields = lobFields.filter((field) => this[field]);

		// If more than one field is true, or none are true, return 'Sonstiges'
		if (trueFields.length !== 1) {
			return 'Übergreifend';
		}

		// Extract label from field name: 'lobSach' -> 'Sach'
		return trueFields[0].substring(3);
	}
	@computed
	get geschaeftsfeld() {
		const baFields = ['baFirmen', 'baPrivat', 'baSonstige'] as const;
		const trueFields = baFields.filter((field) => this[field]);

		// If more than one field is true, or none are true, return 'Sonstige'
		if (trueFields.length !== 1) {
			return 'Übergreifend';
		}

		// Extract label from field name: 'baFirmen' -> 'Firmen'
		return trueFields[0].substring(2);
	}

	@computed
	get campaignTags(): Array<{ text: string; color: string }> {
		const tagDefinitions: Array<{ property: keyof AkumaCampaignModel; text: string; color: string }> = [
			// Campaign type tags
			{ property: 'topCampaign', text: 'Top', color: 'primary' },
			{ property: 'focusCampaign', text: 'Focus', color: 'green' },

			// LOB (Line of Business) tags
			// { property: 'lobSach', text: 'Sach', color: 'yellow' },
			// { property: 'lobLeben', text: 'Leben', color: 'yellow' },
			// { property: 'lobKranken', text: 'Kranken', color: 'yellow' },
			// { property: 'lobAgi', text: 'AGI', color: 'yellow' },
			// { property: 'lobWuestenrot', text: 'Wüstenrot', color: 'yellow' },
			// { property: 'lobSonstiges', text: 'Sonstiges', color: 'yellow' },
			// { property: 'lobUebergreifend', text: 'Übergreifend', color: 'yellow' },

			// // Business area tags
			// { property: 'baFirmen', text: 'Firmen', color: 'info' },
			// { property: 'baPrivat', text: 'Privat', color: 'info' },
			// { property: 'baSonstige', text: 'Sonstige', color: 'info' },
		];

		return tagDefinitions.filter((def) => this[def.property] === true).map((def) => ({ text: def.text, color: def.color }));
	}
}

export class AkumaCampaignStore extends AgtAnalyticsStore {
	constructor(session: SessionStore) {
		super(session, {
			tableName: 'akumaaktionen_campaigns',
			defaultColumns: [
				{ column: 'campaign_number', aggregateFunction: AggregateFunction.NONE },
				{ column: 'campaign_name', aggregateFunction: AggregateFunction.NONE },
				{ column: 'focus_campaign', aggregateFunction: AggregateFunction.NONE },
				{ column: 'top_campaign', aggregateFunction: AggregateFunction.NONE },
				{ column: 'lob_uebergreifend', aggregateFunction: AggregateFunction.NONE },
				{ column: 'lob_sach', aggregateFunction: AggregateFunction.NONE },
				{ column: 'lob_leben', aggregateFunction: AggregateFunction.NONE },
				{ column: 'lob_kranken', aggregateFunction: AggregateFunction.NONE },
				{ column: 'lob_agi', aggregateFunction: AggregateFunction.NONE },
				{ column: 'lob_wuestenrot', aggregateFunction: AggregateFunction.NONE },
				{ column: 'lob_sonstiges', aggregateFunction: AggregateFunction.NONE },
				{ column: 'ba_firmen', aggregateFunction: AggregateFunction.NONE },
				{ column: 'ba_privat', aggregateFunction: AggregateFunction.NONE },
				{ column: 'ba_sonstige', aggregateFunction: AggregateFunction.NONE },
				{ column: 'campaign_start_date', aggregateFunction: AggregateFunction.NONE },
				{ column: 'campaign_end_date', aggregateFunction: AggregateFunction.NONE },
			],
		});
		this.queryName = '?akumaCampaigns';
	}

	async getAllCampaigns(): Promise<AkumaCampaignModel[]> {
		const results = await this.query();
		return results.map((data) => new AkumaCampaignModel(data));
	}
}
